<script>
    import { slide } from 'svelte/transition';

    /** ['sensor-kit-sN'] — two-way bound with the parent form */
    export let equipment = [];
    export let error = null;
    /** Read-only: short description of the chosen size for the collapsed accordion header */
    export let summary = '';
    export let preselectedSize = null;
    /** 'M' hides the bra band path since it does not apply */
    export let gender = 'F';

    const VIDEO_URL = 'https://cdn.oto.coach/static-content/oto/chest_strap_onboarding.mp4';

    // Single source of truth for the chart, the band chips and the measurement slider.
    const sizeRanges = [
        { size: 0, mm: 'less than 420', inches: 'less than 16.5', braSize: '28 - 32', bands: [28, 30, 32], mmMin: 380, mmMax: 420 },
        { size: 1, mm: '421 - 447', inches: '16.5 - 17.6', braSize: '34 - 36', bands: [34, 36], mmMin: 421, mmMax: 447 },
        { size: 2, mm: '448 - 473', inches: '17.6 - 18.6', braSize: '38', bands: [38], mmMin: 448, mmMax: 473 },
        { size: 3, mm: '474 - 501', inches: '18.6 - 19.7', braSize: '40', bands: [40], mmMin: 474, mmMax: 501 },
        { size: 4, mm: '502 - 536', inches: '19.7 - 21.1', braSize: '42', bands: [42], mmMin: 502, mmMax: 536 },
        { size: 5, mm: '537 - 578', inches: '21.1 - 22.7', braSize: '44 - 46', bands: [44, 46], mmMin: 537, mmMax: 578 },
    ];

    const MM_MIN = sizeRanges[0].mmMin;
    const MM_MAX = sizeRanges[sizeRanges.length - 1].mmMax;

    const bands = sizeRanges.flatMap(range => range.bands.map(band => ({ band, size: range.size })));

    function skuFor(size) {
        return `sensor-kit-s${size}`;
    }

    function sizeFromSku(sku) {
        const match = /sensor-kit-s(\d)/.exec(sku || '');
        return match ? parseInt(match[1]) : null;
    }

    let selectedSize = preselectedSize !== null ? preselectedSize : sizeFromSku(equipment[0]);
    let selectedBand = null;
    let measuring = gender === 'M';
    let guideTab = 'chart';
    let useMillimetres = false;
    let measurementMm = 474;

    if (selectedSize !== null && selectedSize !== undefined) {
        const range = sizeRanges.find(r => r.size === selectedSize);
        if (range) {
            if (range.bands.length === 1) selectedBand = range.bands[0];
            measurementMm = Math.round((range.mmMin + range.mmMax) / 2);
        }
    }

    $: resolvedRange = sizeRanges.find(r => r.size === selectedSize) || null;
    $: summary = resolvedRange
        ? (gender === 'M'
            ? `Belt size ${resolvedRange.size}`
            : `Belt size ${resolvedRange.size} · band ${selectedBand || resolvedRange.braSize}`)
        : '';
    $: measuredRange = sizeRanges.find(r => measurementMm <= r.mmMax) || sizeRanges[sizeRanges.length - 1];
    $: inches = (measurementMm / 25.4).toFixed(1);

    function applySize(size) {
        error = null;
        selectedSize = size;
        equipment = [skuFor(size)];
    }

    function selectBand(band, size) {
        selectedBand = band;
        const range = sizeRanges.find(r => r.size === size);
        if (range) measurementMm = Math.round((range.mmMin + range.mmMax) / 2);
        applySize(size);
    }

    function stepMeasurement(deltaMm) {
        measurementMm = Math.min(MM_MAX, Math.max(MM_MIN, measurementMm + deltaMm));
        selectedBand = null;
        applySize(measuredRange.size);
    }

    function onSliderInput() {
        selectedBand = null;
        applySize(measuredRange.size);
    }
</script>

<div class="fit-selector">
    <div class="guide-wrap">
        <div class="guide-head">
            <p class="field-label">Sizing guide</p>
            <div class="guide-tabs">
                <button type="button" class:on={guideTab === 'chart'} on:click={() => (guideTab = 'chart')}>Chart</button>
                <button type="button" class:on={guideTab === 'video'} on:click={() => (guideTab = 'video')}>How to measure</button>
            </div>
        </div>

        {#if guideTab === 'chart'}
            <table class="size-table">
                <thead>
                    <tr>
                        <th>Size</th>
                        <th>Bra band</th>
                        <th>Millimetres</th>
                        <th>Inches</th>
                    </tr>
                </thead>
                <tbody>
                    {#each sizeRanges as { size, braSize, mm, inches: inchRange }}
                        <tr class:current={selectedSize === size}>
                            <td>{size}</td>
                            <td>{braSize}</td>
                            <td>{mm} mm</td>
                            <td>{inchRange} in</td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        {:else}
            <div class="video-wrap">
                <video src={VIDEO_URL} controls preload="metadata">
                    <track kind="captions" />
                </video>
            </div>
        {/if}

        <p class="chart-contact">Contact us if you can't find your size: support@oto.coach</p>
    </div>

    {#if !measuring && gender !== 'M'}
        <div class="band-block">
            <p class="field-label">What's your bra band size?</p>
            <div class="band-chips">
                {#each bands as { band, size }}
                    <button
                        type="button"
                        class="band-chip"
                        class:selected={selectedBand === band}
                        on:click={() => selectBand(band, size)}
                    >{band}</button>
                {/each}
            </div>
        </div>
    {/if}

    {#if measuring}
        <div class="measure-block" transition:slide={{ duration: 200 }}>
            <div class="measure-head">
                <p class="field-label">Chest width, straight across</p>
                <div class="unit-toggle">
                    <button type="button" class:on={!useMillimetres} on:click={() => (useMillimetres = false)}>in</button>
                    <button type="button" class:on={useMillimetres} on:click={() => (useMillimetres = true)}>mm</button>
                </div>
            </div>
            <div class="stepper">
                <button type="button" on:click={() => stepMeasurement(useMillimetres ? -1 : -3)} aria-label="Decrease">&minus;</button>
                <div class="stepper-value">
                    {#if useMillimetres}
                        {measurementMm}<span class="unit">mm</span>
                    {:else}
                        {inches}<span class="unit">in</span>
                    {/if}
                </div>
                <button type="button" on:click={() => stepMeasurement(useMillimetres ? 1 : 3)} aria-label="Increase">+</button>
            </div>
            <input
                class="slider"
                type="range"
                min={MM_MIN}
                max={MM_MAX}
                step="1"
                bind:value={measurementMm}
                on:input={onSliderInput}
            />
            <p class="measure-hint">All measurements are taken straight across (side to side), not around your chest.</p>
        </div>
    {/if}

    {#if resolvedRange}
        <div class="result-card">
            <div class="result-badge">{resolvedRange.size}</div>
            <div>
                <p class="result-eyebrow">Your belt size</p>
                <p class="result-title">Size {resolvedRange.size} fits a {resolvedRange.braSize} band</p>
                <p class="result-sub">{resolvedRange.mm} mm &middot; {resolvedRange.inches} in across the chest</p>
            </div>
        </div>
    {:else}
        <div class="result-placeholder">
            <i class="fa-solid fa-ruler-horizontal" aria-hidden="true"></i>
            {gender === 'M' ? 'Measure across your chest and we\'ll size the belt for you' : 'Pick a band size and we\'ll size the belt for you'}
        </div>
    {/if}

    {#if gender !== 'M'}
        <button type="button" class="disclosure" on:click={() => (measuring = !measuring)}>
            <span>{measuring ? 'Use my bra band size instead' : 'Don\'t wear a bra, or between sizes?'}</span>
            <i class="fa-solid fa-chevron-down" class:rotated={measuring} aria-hidden="true"></i>
        </button>
    {/if}

    {#if error}
        <p class="fit-error">{error}</p>
    {/if}
</div>

<style lang="scss">
    .fit-selector {
        display: flex;
        flex-direction: column;
        gap: 12px;
        text-align: left;
        color: #1f2937;
    }

    .field-label {
        font-size: 12px;
        color: #6b7280;
        margin: 0 0 8px;
    }

    .band-chips {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(52px, 1fr));
        gap: 8px;
    }

    .band-chip {
        border: 1px solid #dfe3e8;
        border-radius: 10px;
        background: white;
        padding: 10px 0;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.15s ease;

        &:hover {
            border-color: #b9c0c9;
        }

        &.selected {
            border-color: var(--secondary-color, #2196c9);
            box-shadow: inset 0 0 0 1px var(--secondary-color, #2196c9);
            color: var(--secondary-color, #2196c9);
            font-weight: 700;
        }
    }

    .measure-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;

        .field-label {
            margin-bottom: 8px;
        }
    }

    .unit-toggle,
    .guide-tabs {
        display: inline-flex;
        background: #f1f2f4;
        border-radius: 8px;
        padding: 2px;

        button {
            border: none;
            background: none;
            border-radius: 6px;
            padding: 4px 10px;
            font-size: 12px;
            color: #6b7280;
            cursor: pointer;

            &.on {
                background: white;
                color: #1f2937;
                font-weight: 600;
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
            }
        }
    }

    .unit-toggle {
        margin-bottom: 8px;
    }

    .guide-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 10px;

        .field-label {
            margin-bottom: 0;
        }
    }

    .video-wrap {
        border-radius: 12px;
        overflow: hidden;
        background: #1b1f24;

        video {
            width: 100%;
            aspect-ratio: 16 / 9;
            display: block;
        }
    }

    .stepper {
        display: flex;
        align-items: stretch;
        border: 1px solid #dfe3e8;
        border-radius: 10px;
        overflow: hidden;

        button {
            width: 54px;
            background: #f7f8fa;
            border: none;
            font-size: 20px;
            color: #4b5563;
            cursor: pointer;

            &:hover {
                background: #eef0f3;
            }
        }
    }

    .stepper-value {
        flex: 1;
        text-align: center;
        padding: 12px 0;
        font-size: 20px;
        font-weight: 600;
        background: white;

        .unit {
            font-size: 12px;
            font-weight: 500;
            color: #9ca3af;
            margin-left: 3px;
        }
    }

    .slider {
        width: 100%;
        margin: 12px 0 0;
        accent-color: var(--secondary-color, #2196c9);
    }

    .measure-hint {
        font-size: 11px;
        color: #9ca3af;
        margin: 6px 0 0;
    }

    .result-card {
        display: flex;
        align-items: center;
        gap: 14px;
        border: 1px solid #dceaf5;
        background: #f4fafd;
        border-radius: 12px;
        padding: 14px;
    }

    .result-badge {
        width: 44px;
        height: 44px;
        border-radius: 10px;
        background: var(--secondary-color, #2196c9);
        color: var(--over-secondary-color, white);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        font-weight: 700;
        flex-shrink: 0;
    }

    .result-eyebrow {
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--secondary-color, #2196c9);
        margin: 0;
    }

    .result-title {
        font-size: 14px;
        font-weight: 600;
        margin: 2px 0 0;
    }

    .result-sub {
        font-size: 12px;
        color: #6b7280;
        margin: 2px 0 0;
    }

    .result-placeholder {
        display: flex;
        align-items: center;
        gap: 10px;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        padding: 14px;
        font-size: 13px;
        color: #9ca3af;
    }

    .disclosure {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        background: white;
        padding: 13px 14px;
        font-size: 13px;
        color: #4b5563;
        cursor: pointer;

        &:hover {
            background: #f9fafb;
        }

        i {
            color: #9ca3af;
            transition: transform 0.2s ease;

            &.rotated {
                transform: rotate(180deg);
            }
        }
    }

    .guide-wrap {
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        padding: 12px;
    }

    .size-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 12px;

        th {
            font-size: 10px;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            color: #9ca3af;
            font-weight: 600;
        }

        th,
        td {
            padding: 7px 4px;
            text-align: center;
            border-bottom: 1px solid #f1f2f4;
        }

        tr.current td {
            background: #f4fafd;
            font-weight: 600;
        }
    }

    .chart-contact {
        font-size: 10px;
        color: #9ca3af;
        margin: 8px 0 0;
        text-align: center;
    }

    .fit-error {
        font-size: 12px;
        color: #dc2626;
        margin: 0;
    }
</style>
